import { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faXmark } from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../AuthContext";
import "./commentpopup.css";

const CommentPopup = ({ onClose, chapterId, lessonId, questionIndex, onNewComment }) => {
  const auth = useAuth();
  const currentUser = auth?.user;
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [activeTab, setActiveTab] = useState("popular");
  const inputRef = useRef(null);
  const replyInputRef = useRef(null);
  const [replyTarget, setReplyTarget] = useState(null);
  const [replyText, setReplyText] = useState("");

  const apiUrl = process.env.REACT_APP_API_URL;

  // 🔹 1. ÎNCARCĂ COMENTARIILE DIN BACKEND
  useEffect(() => {
    fetch(`${apiUrl}/comments/${chapterId}/${lessonId}/${questionIndex}`, {
      credentials: "include", // trimite cookie JWT
    })
      .then((res) => {
        if (res.status === 401) return []; // dacă userul nu e logat
        return res.json();
      })
      .then((data) => setComments(data))
      .catch((err) => console.error("Eroare încărcare comentarii:", err));
  }, [chapterId, lessonId, questionIndex, apiUrl]);

  const submitComment = async (textToSend) => {
    if (!textToSend.trim()) return;

    const body = {
      chapter_id: chapterId,
      lesson_id: lessonId,
      question_index: questionIndex,
      comment_text: textToSend,
    };

    const res = await fetch(`${apiUrl}/comments`, {
      method: "POST",
      credentials: "include", // trimite cookie JWT
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (res.status === 401) {
      alert("Trebuie să fii autentificat pentru a posta comentarii.");
      return;
    }

    const saved = await res.json();

    // comentariul nou apare instant
    setComments((prev) => [saved, ...prev]);

    // trimite notificare către FeedbackPopup pentru a actualiza iconița
    if (onNewComment) onNewComment();

    return saved;
  };

  // 🔹 2. TRIMITE UN COMMENT LA BACKEND
  const handlePost = async () => {
    const posted = await submitComment(newComment);
    if (!posted) return;
    setNewComment("");
  };

  const getAvatarInitial = (username) => {
    if (!username) return "U";
    return username.trim().charAt(0).toUpperCase();
  };

  const getAvatarUrl = (profilePicture) => {
    if (!profilePicture) return "";

    const trimmedPicture = profilePicture.trim();
    if (!trimmedPicture) return "";

    if (
      trimmedPicture.startsWith("http://") ||
      trimmedPicture.startsWith("https://") ||
      trimmedPicture.startsWith("data:")
    ) {
      return trimmedPicture;
    }

    if (trimmedPicture.startsWith("/") && apiUrl) {
      return `${apiUrl}${trimmedPicture}`;
    }

    return trimmedPicture;
  };

  const getPopularityScore = (comment) => {
    return Number(
      comment.likes_count ??
      comment.upvotes ??
      comment.votes ??
      comment.replies_count ??
      0
    );
  };

  const formatRelativeTime = (createdAt) => {
    if (!createdAt) return "";

    const createdDate = new Date(createdAt);
    if (Number.isNaN(createdDate.getTime())) return "";

    const diffMs = Date.now() - createdDate.getTime();
    const minute = 60 * 1000;
    const hour = 60 * minute;
    const day = 24 * hour;
    const week = 7 * day;
    const month = 30 * day;
    const year = 365 * day;

    if (diffMs < minute) return "acum";
    if (diffMs < hour) {
      const value = Math.floor(diffMs / minute);
      return `${value} minut${value > 1 ? "e" : ""}`;
    }
    if (diffMs < day) {
      const value = Math.floor(diffMs / hour);
      return `${value} or${value > 1 ? "e" : "ă"}`;
    }
    if (diffMs < week) {
      const value = Math.floor(diffMs / day);
      return `${value} zi${value > 1 ? "le" : ""}`;
    }
    if (diffMs < month) {
      const value = Math.floor(diffMs / week);
      return `${value} săptămân${value > 1 ? "i" : "ă"}`;
    }
    if (diffMs < year) {
      const value = Math.floor(diffMs / month);
      return `${value} lun${value > 1 ? "i" : "ă"}`;
    }
    const value = Math.floor(diffMs / year);
    return `${value} ${value > 1 ? "ani" : "an"}`;
  };

  const handleReply = (comment) => {
    setReplyTarget(comment);
    setReplyText("");
    setTimeout(() => replyInputRef.current?.focus(), 0);
  };

  const handleSendReply = async () => {
    if (!replyTarget || !replyText.trim()) return;
    const safeUsername = replyTarget.username || "Utilizator";
    const posted = await submitComment(`@${safeUsername} ${replyText.trim()}`);
    if (!posted) return;
    setReplyText("");
    setReplyTarget(null);
  };

  const sortedComments = [...comments].sort((first, second) => {
    const firstDate = new Date(first.created_at).getTime() || 0;
    const secondDate = new Date(second.created_at).getTime() || 0;

    if (activeTab === "recent") {
      return secondDate - firstDate;
    }

    const firstPopularity = getPopularityScore(first);
    const secondPopularity = getPopularityScore(second);
    if (secondPopularity !== firstPopularity) {
      return secondPopularity - firstPopularity;
    }

    return secondDate - firstDate;
  });

  return (
    <div className="comment-popup-overlay">
      <div className="comment-popup">

        {/* Buton închidere */}
        <button className="close-popup-btn" onClick={onClose}>
          <FontAwesomeIcon icon={faXmark} size="lg" />
        </button>

        <div className="comment-top-row">
          <button className="comment-hide-btn" type="button" onClick={onClose}>
            Ascunde <span className="comment-hide-arrow">›</span>
          </button>
          <span className="comment-total">{comments.length} comentarii</span>
        </div>

        <div className="comment-title-row">
          <h2 className="comment-header">Comentarii</h2>
        </div>

        {/* Input + Post */}
        <div className="comment-input-row">
          <textarea
            placeholder="Scrie un comentariu..."
            className="comment-input"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            rows={2}
            ref={inputRef}
          />
          <button className="comment-post-btn" onClick={handlePost}>
            Post
          </button>
        </div>

        <div className="comment-tabs" role="tablist" aria-label="Sortare comentarii">
          <button
            className={`comment-tab ${activeTab === "popular" ? "active" : ""}`}
            onClick={() => setActiveTab("popular")}
            type="button"
          >
            Popular
          </button>
          <button
            className={`comment-tab ${activeTab === "recent" ? "active" : ""}`}
            onClick={() => setActiveTab("recent")}
            type="button"
          >
            Recent
          </button>
        </div>

        {/* Listă comentarii */}
        <div className="comment-list">
          {comments.length === 0 && (
            <p className="no-comments">Fii primul care comentează! ✍️</p>
          )}

          {sortedComments.map((c) => (
            <div key={c.id} className="comment-item">
              <div className="comment-avatar">
                {getAvatarUrl(c.profile_picture) ? (
                  <img
                    src={getAvatarUrl(c.profile_picture)}
                    alt={c.username || "Utilizator"}
                    className="comment-avatar-image"
                  />
                ) : (
                  getAvatarInitial(c.username)
                )}
              </div>
              <div className="comment-body">
                <div className="comment-user-row">
                  <b className="comment-username">{c.username || "Utilizator"}</b>
                  <span className="comment-time">{formatRelativeTime(c.created_at)}</span>
                </div>

                <p className="comment-text">{c.comment_text}</p>

                <div className="comment-meta-row">
                  <span className="comment-votes">↑ {getPopularityScore(c)}</span>
                  <button
                    className="comment-reply"
                    type="button"
                    onClick={() => handleReply(c)}
                  >
                    Răspunde
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {replyTarget && (
          <div className="reply-composer-row">
            <div className="reply-avatar" aria-hidden="true">
              {getAvatarUrl(currentUser?.profile_picture) ? (
                <img
                  src={getAvatarUrl(currentUser?.profile_picture)}
                  alt={currentUser?.username || "Utilizator"}
                  className="reply-avatar-image"
                />
              ) : currentUser?.username ? (
                getAvatarInitial(currentUser.username)
              ) : (
                <FontAwesomeIcon icon={faUser} />
              )}
            </div>

            <div className="reply-composer-input-wrap">
              <input
                ref={replyInputRef}
                type="text"
                className="reply-composer-input"
                placeholder="Scrie un răspuns..."
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSendReply();
                  }
                }}
              />
              <button
                className="reply-cancel-btn"
                type="button"
                onClick={() => {
                  setReplyTarget(null);
                  setReplyText("");
                }}
              >
                Renunță
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default CommentPopup;
